// Word-order-independent matching for every picker in the app.
//
// WHY. People do not remember which way round a name is stored. "Sanjay Ailani"
// and "Ailani Sanjay" are the same person, and the database holds one of them —
// so a substring filter (the default in MUI's Autocomplete) matches whichever
// order happens to be stored and silently finds NOTHING for the other. The user
// then concludes the person is not in the list.
//
// The rule here: split the query into words, and match when EVERY word appears
// somewhere in the option — in any order, as a prefix of any word of it.
//
//   query "sanjay ailani"  vs  "Ailani Sanjay Hareshlal"   -> match
//   query "ailani sanjay"  vs  "Ailani Sanjay Hareshlal"   -> match
//   query "ail san"        vs  "Ailani Sanjay Hareshlal"   -> match
//   query "sanjay patel"   vs  "Ailani Sanjay Hareshlal"   -> no match
//
// The same applies to devices ("touch 4" finding "4 TOUCH") and to anything
// else searched by name, which is why this lives in core rather than in one
// screen.

/** Lowercase words, punctuation and separators discarded. */
export const searchTokens = (text) => String(text ?? '')
  .toLowerCase()
  .split(/[^a-z0-9]+/i)
  .filter(Boolean);

/**
 * Does `haystack` match `query`, ignoring word order?
 *
 * An empty query matches everything — a picker that has not been typed into
 * shows its whole list.
 */
export function matchesTokens(haystack, query) {
  const wanted = searchTokens(query);
  if (!wanted.length) return true;
  const have = searchTokens(haystack);
  if (!have.length) return false;
  // Prefix rather than equality, so partial words work while typing.
  return wanted.every((w) => have.some((h) => h.startsWith(w))
    // Substring as a fallback: "4811" should still find "884811", and a unique
    // number is one long token with no word boundaries to prefix-match on.
    || have.some((h) => h.includes(w)));
}

/**
 * A `filterOptions` for MUI Autocomplete.
 *
 * `textOf` returns everything searchable about an option — for a user that is
 * the name AND the mobile number, so either finds them.
 */
export const tokenFilter = (textOf) => (options, { inputValue }) =>
  options.filter((o) => matchesTokens(textOf(o), inputValue));

/**
 * Options for a picker that can also CREATE, and that is too long to draw whole.
 *
 * -> [ …matches (capped), (more marker), …create rows ]
 *
 * ⚠️ A CREATE ROW IS NOT A SEARCH RESULT, and treating it as one has broken
 * this twice. "＋ New user…" is appended LAST to the option list, so:
 *
 *   • capping the drawn matches removed it — against 457 users, the cap of 40
 *     cut the one entry that was not a user at all, and no amount of scrolling
 *     found it;
 *   • FILTERING it alongside the names removed it too, because no person's
 *     name matches "＋ New user…" — so typing somebody who does not exist made
 *     the list say "No options", at the exact moment the option was wanted.
 *
 * So create rows are lifted out BEFORE filtering and appended AFTER the cap:
 * always present, always last, whatever was typed.
 *
 * The cap exists because MUI builds a DOM node per drawn option on every
 * keystroke; 457 two-line rows is what made the box feel frozen. `more` is
 * called with the number hidden so the caller can say so — a filter that
 * silently stops at N is how somebody you know exists appears not to.
 */
export function pickerOptions(options, inputValue, {
  textOf, isCreate, limit = 40, more,
}) {
  const all = options || [];
  const creates = isCreate ? all.filter(isCreate) : [];
  const pool = isCreate ? all.filter((o) => !isCreate(o)) : all;
  const hits = pool.filter((o) => matchesTokens(textOf(o), inputValue));
  if (hits.length <= limit) return [...hits, ...creates];
  const hidden = hits.length - limit;
  const marker = more ? [more(hidden)] : [];
  return [...hits.slice(0, limit), ...marker, ...creates];
}
